<script setup lang="ts">
import { ref, computed, type Ref, onMounted } from 'vue'
import AppModal from '@/components/ui/AppModal.vue'
import {useFormsStore} from "@/domains/home/services/nir/configurations/stores/forms.ts";
import {EntityType} from "@/domains/home/services/nir/configurations/types/forms.ts";
import {dateTimeFormat} from "@/commons/composables/transformations.ts";
import {useConfirmDialog} from "@/composables/useConfirmDialog";
import {useToast} from "@/composables";

const createModalOpen = ref(false)
const updateModalOpen = ref(false)
const selectedEditId = ref('')
const parentIdFilter = ref('')
const loading = ref(false)
const creating = ref(false)

const form = ref({
  name: '',
  description: '',
  parentId: '',
})

const editForm = ref({
  id: '',
  name: '',
  description: '',
  parentId: '',
})

const store = useFormsStore()
const { showConfirm } = useConfirmDialog()
const { successToast, errorToast } = useToast()
onMounted(() => {
  fetch()
})

function fetch() {
  loading.value = true
  store.fetchEntityTypes(parentIdFilter.value).then(() => {
    loading.value = false
  })

  store.fetchPrimaryEntityTypes()
}

function getParentName(parentId: string) {
  if (!parentId) return '-'
  const parent = store.entityTypes?.find((et) => et.id === parentId)
  return parent?.name || '-'
}

function add() {
  resetForm()
  createModalOpen.value = true
}

function submit() {
  creating.value = true
  const payload = {
    name: form.value.name,
    description: form.value.description,
    ...(form.value.parentId && { parentId: form.value.parentId }),
  }
  store
    .createEntityType(payload)
    .then(() => {
      creating.value = false
      createModalOpen.value = false
      fetch()
    })
    .catch(() => {
      creating.value = false
    })
}

function update() {
  creating.value = true
  const payload = {
    id: editForm.value.id,
    name: editForm.value.name,
    description: editForm.value.description,
    ...(editForm.value.parentId && { parentId: editForm.value.parentId }),
  }
  store
    .updateEntityType(payload)
    .then(() => {
      creating.value = false
      updateModalOpen.value = false
      fetch()
    })
    .catch(() => {
      creating.value = false
    })
}

function resetForm() {
  form.value = {
    name: '',
    description: '',
    parentId: '',
  }

  editForm.value = {
    id: '',
    name: '',
    description: '',
    parentId: '',
  }
}

function closeModal() {
  createModalOpen.value = false
  updateModalOpen.value = false
  resetForm()
}

function editItem(item: EntityType) {
  editForm.value.id = item.id
  editForm.value.name = item.name
  editForm.value.description = item.description
  editForm.value.parentId = item.parentId
  updateModalOpen.value = true
}

async function deleteItem(item: EntityType) {
  const confirmed = await showConfirm(
    `Are you sure you want to delete the entity type "${item.name}"?`,
    { title: 'Delete Entity Type', okTitle: 'Delete', variant: 'danger' },
  )
  if (!confirmed) return

  store
    .deleteEntityType(item.id)
    .then(() => {
      successToast('Success', `Entity type "${item.name}" was deleted`)
      fetch()
    })
    .catch((error) => {
      errorToast(
        'Error',
        error.response?.data?.error?.error ?? 'Failed to delete entity type',
      )
    })
}
</script>

<template>
  <div class="flex-1 flex flex-col">
    <!-- Action Bar -->
    <div class="flex items-center gap-2 justify-end mb-4">
      <span>
        <select
          v-model="parentIdFilter"
          @change="fetch"
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
        >
          <option value="">Select Type</option>
          <option
            v-for="entityType in store.primaryEntityTypes"
            :key="entityType.id"
            :value="entityType.id"
          >
            {{ entityType.name }}
          </option>
        </select>
      </span>
      <button
        @click="add"
        class="btn-primary bg-primary hover:bg-primary-700 text-white px-4 py-2 rounded text-sm font-medium transition flex items-center space-x-2"
      >
        <i class="fa-solid fa-plus"></i>
        <span>Add Entity Type</span>
      </button>
    </div>

    <!-- Form Fields Table -->
    <div class="bg-white rounded shadow flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Name
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Parent Type
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Description
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Date
              </th>
              <th
                class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200"
              ></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in store.entityTypes"
              :key="item.id"
              class="hover:bg-gray-50 transition"
            >
              <td class="py-3 px-4 border-b border-gray-200">
                <span class="text-gray-900 font-medium">{{ item.name }}</span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200">
                <span class="text-gray-600 text-xs">
                  <i class="fa-solid fa-sitemap mr-1 text-gray-400"></i>
                  {{ getParentName(item.parentId) }}
                </span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200">
                <span class="px-2 py-1 text-gray-400">
                  {{ item.description }}
                </span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200 text-gray-400">
                {{ dateTimeFormat(item.createdAt) }}
              </td>
              <td class="py-3 px-4 border-b border-gray-200">
                <div class="flex items-center justify-center space-x-2">
                  <button
                    @click="editItem(item)"
                    class="btn-sm-primary"
                    title="Edit">
                    <i class="fa-solid fa-edit"></i>
                  </button>
                  <button
                    @click="deleteItem(item)"
                    class="btn-sm-danger"
                    title="Delete">
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            <!-- Empty State -->
            <tr v-if="!store.entityTypes || store.entityTypes.length === 0">
              <td colspan="5" class="py-12 text-center">
                <div class="flex flex-col items-center justify-center text-gray-500">
                  <i class="fa-solid fa-sitemap text-4xl mb-3 text-gray-300"></i>
                  <p class="text-lg font-medium">No entity types found</p>
                  <p class="text-sm">Add a new entity type to get started</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create Entity Type Modal -->
    <AppModal v-model="createModalOpen" md>
      <div class="flex flex-col p-6 bg-white">
        <h2 class="text-lg font-bold text-gray-900 mb-4">Add Entity Type</h2>

        <form @submit.prevent="submit" class="space-y-4">
          <!-- Name -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-tag text-gray-400 mr-1"></i>
              Name *
            </label>
            <input
              v-model="form.name"
              type="text"
              required
              placeholder="Enter entity type name"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          <!-- Description -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-align-left text-gray-400 mr-1"></i>
              Description *
            </label>
            <textarea
              v-model="form.description"
              required
              rows="4"
              placeholder="Enter entity type description"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            ></textarea>
          </div>

          <!-- Parent Entity Type -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-sitemap text-gray-400 mr-1"></i>
              Parent Entity Type
            </label>
            <select
              v-model="form.parentId"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">None (Root Level)</option>
              <option
                v-for="entityType in store.primaryEntityTypes"
                :key="entityType.id"
                :value="entityType.id"
              >
                {{ entityType.name }}
              </option>
            </select>
            <p class="text-xs text-gray-500 mt-1">
              Optional: Select a parent entity type to create a hierarchical relationship
            </p>
          </div>

          <!-- Form Actions -->
          <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              @click="closeModal"
              class="btn-danger"
            >
              <i class="fas fa-times mr-1"></i>
              Cancel
            </button>
            <button
              type="submit"
              :disabled="creating"
              class="btn-primary px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fa-solid fa-check mr-1"></i>
              <span v-if="!creating">Create Entity Type</span>
              <span v-else>Creating...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>

    <AppModal v-model="updateModalOpen" md>
      <div class="flex flex-col p-6 bg-white">
        <h2 class="text-lg font-bold text-gray-900 mb-4">Edit Entity Type</h2>

        <form @submit.prevent="update" class="space-y-4">
          <!-- Name -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-tag text-gray-400 mr-1"></i>
              Name *
            </label>
            <input
              v-model="editForm.name"
              type="text"
              required
              placeholder="Enter entity type name"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          <!-- Description -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-align-left text-gray-400 mr-1"></i>
              Description *
            </label>
            <textarea
              v-model="editForm.description"
              required
              rows="4"
              placeholder="Enter entity type description"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            ></textarea>
          </div>

          <!-- Parent Entity Type -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-sitemap text-gray-400 mr-1"></i>
              Parent Entity Type
            </label>
            <select
              v-model="editForm.parentId"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">None (Root Level)</option>
              <option
                v-for="entityType in store.primaryEntityTypes"
                :key="entityType.id"
                :value="entityType.id"
              >
                {{ entityType.name }}
              </option>
            </select>
            <p class="text-xs text-gray-500 mt-1">
              Optional: Select a parent entity type to create a hierarchical relationship
            </p>
          </div>

          <!-- Form Actions -->
          <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              @click="closeModal"
              class="btn-danger"
            >
              <i class="fas fa-times mr-1"></i>
              Cancel
            </button>
            <button
              type="submit"
              :disabled="creating"
              class="btn-primary px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fa-solid fa-check mr-1"></i>
              <span v-if="!creating">Update Entity Type</span>
              <span v-else>Updating...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>
  </div>
</template>

<style scoped>
table {
  border-collapse: separate;
  border-spacing: 0;
}
</style>
